/**
 * Calculate how many SMS parts (segments) a message will be split into.
 *
 * Stand-alone, dependency-free port of Tellescope's `message_parts_in_sms`.
 * Mirrors the billing-usage calculation used by the communications service.
 *
 * Encoding rules:
 *  - GSM 3.38 (7-bit): 160 chars for a single message, 153 chars per segment
 *    when multipart. Chars `^ { } [ ] | €` occupy TWO GSM slots each.
 *  - Unicode / UCS-2 (any non-GSM char, or code point > 255): 70 chars for a
 *    single message, 67 chars per segment when multipart.
 */

// A message is Unicode-encoded if it contains any char outside the GSM 3.38 set.
// Ref: https://www.regextester.com/99623
const HAS_NON_GSM_3_38_REGEX =
  /[^A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#$%&'()*+,./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]*/;

// GSM chars that count as two characters against the segment length.
// Ref: https://docs.aws.amazon.com/pinpoint/latest/userguide/channels-sms-limitations.html#channels-sms-limitations-characters-gsm-alphabet
const DOUBLE_LENGTH_GSM_REGEX = /[\^{}\\[\]|€]/g; // global, so `match` returns every occurrence

const hasUnicodeCharacter = (sms: string): boolean => {
  for (let i = 0; i < sms.length; i++) {
    if (sms.charCodeAt(i) > 255) return true;
  }
  return false;
};

export const getSMSParts = (sms: string): number => {
  let singleMessageLength = 160;
  let multiPartLength = 153;
  let smsLength = sms.length;

  if (HAS_NON_GSM_3_38_REGEX.test(sms) || hasUnicodeCharacter(sms)) {
    // Unicode / UCS-2 encoding
    singleMessageLength = 70;
    multiPartLength = 67;
    for (let i = 0; i < sms.length; i++) {
      if (sms.charCodeAt(i) > 255) {
        smsLength -= 1; // a surrogate pair is length 2 in JS; count it as one position
        i++; // skip the second half of the pair
      }
    }
  } else {
    // All GSM 3.38, but some chars occupy two slots
    const longMatches = sms.match(DOUBLE_LENGTH_GSM_REGEX);
    smsLength += longMatches ? longMatches.length : 0;
  }

  return smsLength <= singleMessageLength ? 1 : Math.ceil(smsLength / multiPartLength);
};
